import React, { useEffect, useState } from 'react'
import useFormData from 'hooks/formData'
import InputLabel from 'components/Form/InputLabel'
import Checkbox from 'components/Form/Checkbox'
import Recaptcha, { getCaptchaToken } from 'components/Form/Recaptcha'
import { newsletterSignupApi } from 'apis/newsletter'
import { formDefaults, formValidationRules, getIndustryArray } from './helpers'
import { postTradeContact } from 'apis/trade'
import { REQUEST_SOURCE } from '../../../../../server/constants/newsletter_request_sources'
type Props = {
  closeModal: (...args: Array<any>) => any
}

const Component = (props: Props): React.ReactElement<React.ComponentProps<any>, any> => {
  const { closeModal } = props
  const {
    formData,
    formErrors,
    onChangeCheckbox,
    onChangeInput,
    updateFormData,
    validateFormData,
  } = useFormData({
    initialState: formDefaults,
    formRules: formValidationRules,
  })
  const {
    collector,
    commercial,
    company,
    emailContact,
    firstName,
    healthcare,
    home,
    hospitality,
    lastName,
    residential,
  } = formData
  const { emailContact: emailContactError, firstName: firstNameError } = formErrors
  const [state, handleState] = useState({
    isBlocking: false,
    hasCompleted: false,
  })
  const { isBlocking, hasCompleted } = state

  // Check for email format error on submit
  const onSubmit = async () => {
    if (isBlocking) {
      return
    }

    const isValid = await validateFormData()
    handleState((prevState) => ({ ...prevState, isBlocking: true }))

    if (isValid) {
      const captchaAction = 'trade_form_contact_submit'
      const captchaToken = await getCaptchaToken(captchaAction)
      const email = emailContact
      const industry = getIndustryArray(
        collector,
        commercial,
        healthcare,
        home,
        hospitality,
        residential
      )
      const { success } = await postTradeContact({
        email,
        firstName,
        lastName,
        company,
        industry,
        captchaAction,
        captchaToken,
      })

      if (success === true) {
        handleState((prevState) => ({ ...prevState, isBlocking: false, hasCompleted: true }))
        const params = {
          email,
          requestSource: REQUEST_SOURCE.TRADE_PAGE_CONTACT_US_FORM,
          sourceURL: window.location.href,
        }
        await newsletterSignupApi(params)
        window.dataLayer.push({
          event: 'newsletter signup success',
          email_signup_source: REQUEST_SOURCE.TRADE_PAGE_CONTACT_US_FORM,
        })
        window.dataLayer.push({
          event: 'complete trade contact form',
          form_email: email || '',
          label: 'complete',
        })
      } else {
        handleState((prevState) => ({ ...prevState, isBlocking: false, hasCompleted: false }))
      }
    } else {
      handleState((prevState) => ({ ...prevState, isBlocking: false, hasCompleted: false }))
    }
  }

  const onChangeEmailInput = async (ev: React.SyntheticEvent<HTMLInputElement>) => {
    ev.preventDefault()
    const { name, value } = ev.target
    updateFormData({
      [name]: value,
    })
    delete formErrors[name]
  }

  // Create a delayed effect to close the modal 2 seconds after confirm state
  // is recognized to allow the confirm message to be read.
  useEffect(() => {
    if (hasCompleted === true) {
      closeDelay()
    }
  }, [hasCompleted])
  let timer

  const timerCleanup = () => {
    clearTimeout(timer)
    timer = null
  }

  const closeDelay = (callBack: (...args: Array<any>) => any) => {
    timerCleanup()
    timer = setTimeout(() => {
      closeModal()
    }, 2000)
  }

  return (
    <form
      onSubmit={(ev) => {
        ev.preventDefault()
      }}
      id='contact'
    >
      <p data-type='title'>Work one-on-one with a personal curator</p>

      <div data-type='nameRow'>
        <InputLabel
          error={firstNameError}
          id='firstName'
          maxLength='60'
          name='firstName'
          onChange={onChangeInput}
          placeholder='First Name*'
          type='text'
          value={firstName}
        />

        <InputLabel
          id='lastName'
          maxLength='60'
          name='lastName'
          onChange={onChangeInput}
          placeholder='Last Name'
          type='text'
          value={lastName}
        />
      </div>

      <InputLabel
        error={emailContactError}
        id='emailContact'
        maxLength='60'
        name='emailContact'
        onChange={onChangeEmailInput}
        placeholder='Email Address*'
        type='text'
        value={emailContact}
      />

      <InputLabel
        id='company'
        maxLength='60'
        name='company'
        onChange={onChangeEmailInput}
        placeholder='Company'
        type='text'
        value={company}
      />

      <p data-type='title'>Project Category</p>

      <div data-type='checkboxes'>
        <Checkbox
          checked={hospitality === '1'}
          name='hospitality'
          onChange={onChangeCheckbox}
          text='Hospitality'
          value={hospitality === '1' ? '0' : '1'}
        />
        <Checkbox
          checked={commercial === '1'}
          name='commercial'
          onChange={onChangeCheckbox}
          text='Commercial'
          value={commercial === '1' ? '0' : '1'}
        />
        <Checkbox
          checked={residential === '1'}
          name='residential'
          onChange={onChangeCheckbox}
          text='Multi-Family & Residential'
          value={residential === '1' ? '0' : '1'}
        />
        <Checkbox
          checked={healthcare === '1'}
          name='healthcare'
          onChange={onChangeCheckbox}
          text='Healthcare'
          value={healthcare === '1' ? '0' : '1'}
        />
        <Checkbox
          checked={home === '1'}
          name='home'
          onChange={onChangeCheckbox}
          text='Private Home'
          value={home === '1' ? '0' : '1'}
        />
        <Checkbox
          checked={collector === '1'}
          name='collector'
          onChange={onChangeCheckbox}
          text='Individual Collector or Other'
          value={collector === '1' ? '0' : '1'}
        />
      </div>

      <div data-type='button-recaptcha-container'>
        {hasCompleted ? (
          <p data-type='confirm-contact'>
            Thank you! We've received your information and will respond soon.
          </p>
        ) : (
          <button
            type='button'
            onClick={onSubmit}
            {...(emailContactError || firstName === ''
              ? {
                  disabled: 'disabled',
                }
              : {})}
          >
            Submit
          </button>
        )}

        <div>
          <Recaptcha />
        </div>

        <div>
          <span data-type='subscribe'>
            By submitting this form, you've subscribed to our Trade newsletters. You may unsubscribe
            at any time.
          </span>
        </div>
      </div>
    </form>
  )
}

export default Component
